/**
 * Pure citation-highlight function extracted from PdfRenderer.
 *
 * Matches a search string against the PDF text layer and draws yellow
 * highlight rectangles on the provided canvas. Returns diagnostic info
 * so callers (PdfRenderer for scroll-to, Citation Lab for debugging)
 * can act on the result.
 *
 * Matching strategy (in order):
 * 1. Prefix match (100 chars, then 60 chars) — fast, exact
 * 2. LCS (longest common substring) sliding window — catches paraphrases
 */

import { normalize, lcsMatch } from './textMatchUtils';

export interface HighlightResult {
  /** Whether any text items were highlighted. */
  found: boolean;
  /** Canvas Y of the first highlighted item (for scroll-to logic). */
  firstHighlightY: number | null;
  /** Index in the normalised page text where the match started (-1 if none). */
  matchIndex: number;
  /** The normalised search string (after stripping markers, lowercasing, etc.). */
  normalizedSearch: string;
  /** The normalised full page text. */
  normalizedPageText: string;
  /** Match quality score (0–1). 1.0 = prefix match, 0.x = LCS ratio. */
  matchScore: number;
  /** Which matching strategy succeeded: 'prefix' | 'lcs' | 'none'. */
  matchMethod: 'prefix' | 'lcs' | 'none';
}

/**
 * Minimum LCS ratio to accept as a valid match.
 *
 * Lowered from 0.25 to 0.15 because PDF.js often extracts form/tabular pages
 * in column order rather than reading order, fragmenting the cited text into
 * non-contiguous chunks. LCS only finds the longest CONTIGUOUS run, so the
 * ratio understates the real overlap. A 0.15 ratio against a 400-char search
 * still requires a ~60-char contiguous run, which is essentially impossible
 * by chance — and the UI flags weak matches as "Approximate match" anyway.
 */
const LCS_MATCH_THRESHOLD = 0.15;

/**
 * Minimum word-coverage ratio (distinct ≥4-char search words appearing in the
 * page) used as a rescue when LCS is very low but the cited content is clearly
 * present in re-ordered form.
 */
const WORD_COVERAGE_THRESHOLD = 0.7;
const WORD_COVERAGE_MIN_TOKENS = 8;

export function highlightOnCanvas(
  canvas: HTMLCanvasElement,
  textContent: { items: any[] },
  viewport: { width: number; height: number; scale: number },
  searchStr: string,
): HighlightResult {
  const context = canvas.getContext('2d')!;
  canvas.height = viewport.height;
  canvas.width = viewport.width;
  context.clearRect(0, 0, canvas.width, canvas.height);

  const normalizedSearch = normalize(searchStr);

  const empty: HighlightResult = {
    found: false,
    firstHighlightY: null,
    matchIndex: -1,
    normalizedSearch,
    normalizedPageText: '',
    matchScore: 0,
    matchMethod: 'none',
  };

  if (normalizedSearch.length < 5) return empty;

  // Build full text + position map from text layer items
  let fullText = '';
  const itemPositions: Array<{ start: number; end: number; item: any }> = [];
  textContent.items.forEach((item: any) => {
    if ('str' in item && item.str.trim()) {
      const start = fullText.length;
      fullText += item.str + ' ';
      itemPositions.push({ start, end: fullText.length, item });
    }
  });

  const normalizedFullText = normalize(fullText);

  // ── Strategy 1: Prefix match (fast, exact) ──────────────────────────────
  const searchPrefix = normalizedSearch.substring(0, Math.min(100, normalizedSearch.length));
  let searchIndex = normalizedFullText.indexOf(searchPrefix);
  if (searchIndex === -1 && searchPrefix.length > 60) {
    searchIndex = normalizedFullText.indexOf(normalizedSearch.substring(0, 60));
  }

  if (searchIndex !== -1) {
    const searchEnd = searchIndex + normalizedSearch.length;
    const result = drawHighlights(
      context, itemPositions, fullText, normalizedFullText,
      searchIndex, searchEnd, viewport,
    );
    return {
      found: result.found,
      firstHighlightY: result.firstHighlightY,
      matchIndex: searchIndex,
      normalizedSearch,
      normalizedPageText: normalizedFullText,
      matchScore: 1.0,
      matchMethod: 'prefix',
    };
  }

  // ── Strategy 1b: Skip leading noise (e.g. LLM-added "1. ") ────────────
  // Try matching from the first alphabetic character if the full prefix failed.
  if (normalizedSearch.length > 20) {
    const alphaIdx = normalizedSearch.search(/[a-z]/);
    if (alphaIdx > 0) {
      const trimmedSearch = normalizedSearch.substring(alphaIdx);
      const trimmedPrefix = trimmedSearch.substring(0, Math.min(100, trimmedSearch.length));
      const trimIdx = normalizedFullText.indexOf(trimmedPrefix);
      if (trimIdx !== -1) {
        const searchEnd = trimIdx + trimmedSearch.length;
        const result = drawHighlights(
          context, itemPositions, fullText, normalizedFullText,
          trimIdx, searchEnd, viewport,
        );
        return {
          found: result.found,
          firstHighlightY: result.firstHighlightY,
          matchIndex: trimIdx,
          normalizedSearch,
          normalizedPageText: normalizedFullText,
          matchScore: 0.95,
          matchMethod: 'prefix',
        };
      }
    }
  }

  // ── Strategy 1c: Ellipsis-split prefix match ────────────────────────────
  // LLM excerpts often concatenate non-contiguous passages with "..."
  // Try prefix-matching the first substantial segment before falling to LCS.
  const ellipsisSegments = searchStr.split(/\.{3,}|…/).map(s => normalize(s)).filter(s => s.length >= 20);
  if (ellipsisSegments.length > 1) {
    const firstSeg = ellipsisSegments[0];
    const firstPrefix = firstSeg.substring(0, Math.min(80, firstSeg.length));
    const segIndex = normalizedFullText.indexOf(firstPrefix);
    if (segIndex !== -1) {
      // Find the last segment's position to determine highlight end
      let highlightEnd = segIndex + firstSeg.length;
      for (let i = ellipsisSegments.length - 1; i > 0; i--) {
        const lastPrefix = ellipsisSegments[i].substring(0, Math.min(60, ellipsisSegments[i].length));
        const lastIndex = normalizedFullText.indexOf(lastPrefix, segIndex);
        if (lastIndex !== -1) {
          highlightEnd = lastIndex + ellipsisSegments[i].length;
          break;
        }
      }

      const result = drawHighlights(
        context, itemPositions, fullText, normalizedFullText,
        segIndex, highlightEnd, viewport,
      );
      return {
        found: result.found,
        firstHighlightY: result.firstHighlightY,
        matchIndex: segIndex,
        normalizedSearch,
        normalizedPageText: normalizedFullText,
        matchScore: 0.9,
        matchMethod: 'prefix',
      };
    }
  }

  // ── Strategy 2: LCS fallback (catches paraphrases) ──────────────────────
  const lcs = lcsMatch(normalizedSearch, normalizedFullText);

  if (lcs.ratio >= LCS_MATCH_THRESHOLD && lcs.matchStart >= 0) {
    // Highlight the matched region directly (anchored to where LCS found it,
    // not centred) — avoids highlighting the wrong passage when the LCS
    // match is shorter than the search text.
    const result = drawHighlights(
      context, itemPositions, fullText, normalizedFullText,
      lcs.matchStart, lcs.matchEnd, viewport,
    );
    return {
      found: result.found,
      firstHighlightY: result.firstHighlightY,
      matchIndex: lcs.matchStart,
      normalizedSearch,
      normalizedPageText: normalizedFullText,
      matchScore: lcs.ratio,
      matchMethod: 'lcs',
    };
  }

  // ── Strategy 3: Word-coverage rescue ────────────────────────────────────
  // PDF.js can extract form/tabular pages in column rather than reading order,
  // splitting the cited paragraph into chunks that LCS can't stitch back
  // together. If most distinctive search words still appear in the page text,
  // the citation almost certainly belongs here — highlight the LCS region
  // (best anchor we have) and report as an approximate match.
  const searchTokens = new Set(
    normalizedSearch.split(' ').filter(w => w.length >= 4)
  );
  if (searchTokens.size >= WORD_COVERAGE_MIN_TOKENS) {
    const pageTokens = new Set(normalizedFullText.split(' '));
    let hits = 0;
    for (const w of searchTokens) if (pageTokens.has(w)) hits++;
    const coverage = hits / searchTokens.size;
    if (coverage >= WORD_COVERAGE_THRESHOLD && lcs.matchStart >= 0) {
      const result = drawHighlights(
        context, itemPositions, fullText, normalizedFullText,
        lcs.matchStart, lcs.matchEnd, viewport,
      );
      return {
        found: result.found,
        firstHighlightY: result.firstHighlightY,
        matchIndex: lcs.matchStart,
        normalizedSearch,
        normalizedPageText: normalizedFullText,
        // Report coverage as the score so the UI's "Approximate match" amber
        // dot reflects how much of the citation we could verify.
        matchScore: coverage,
        matchMethod: 'lcs',
      };
    }
  }

  return { ...empty, normalizedPageText: normalizedFullText, matchScore: lcs.ratio };
}

// ── Shared highlighting logic ─────────────────────────────────────────────

function drawHighlights(
  context: CanvasRenderingContext2D,
  itemPositions: Array<{ start: number; end: number; item: any }>,
  fullText: string,
  normalizedFullText: string,
  normStart: number,
  normEnd: number,
  viewport: { width: number; height: number; scale: number },
): { found: boolean; firstHighlightY: number | null } {
  // Build position map: normalised-text index → original fullText index
  const positionMap: number[] = [];
  let prevWasSpace = false;
  for (let i = 0; i < fullText.length; i++) {
    const lower = fullText[i].toLowerCase();
    if (/[^a-z0-9\s]/.test(lower)) continue;
    if (/\s/.test(lower)) {
      if (!prevWasSpace) {
        positionMap.push(i);
        prevWasSpace = true;
      }
      continue;
    }
    prevWasSpace = false;
    positionMap.push(i);
  }

  const originalStart = positionMap[normStart] ?? normStart;
  const originalEnd = positionMap[Math.min(normEnd, positionMap.length - 1)] ?? normEnd;
  let firstHighlightY: number | null = null;

  itemPositions.forEach(({ start, end, item }) => {
    if (end > originalStart && start < originalEnd) {
      const [, , , , x, y] = item.transform;
      const width = item.width;
      const height = item.height || 12;

      context.fillStyle = 'rgba(255, 255, 0, 0.5)';
      context.fillRect(
        x * viewport.scale,
        viewport.height - y * viewport.scale - height * viewport.scale,
        width * viewport.scale,
        height * viewport.scale,
      );

      if (firstHighlightY === null) {
        firstHighlightY = viewport.height - y * viewport.scale - height * viewport.scale;
      }
    }
  });

  return { found: firstHighlightY !== null, firstHighlightY };
}
